import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useToasts } from '../../../cdl/Toast/useToasts';
import { createLubricationChart } from '../../../common/api/clients/vessel.api';
import { queries } from '../../../common/api/queryKeys/queries';
import { translate } from '../../../common/helpers/translate.helper';

import { useLubricationChartProcessing } from './useLubricationChartProcessing';

interface UseLubricationChartUploadOptions {
    vesselId: string;
    customerId?: string;
}

export interface LubricationChartUpload {
    isProcessing: boolean;
    uploadLubricationChart: (file: File) => Promise<number>;
}

export const useLubricationChartUpload = ({
    vesselId,
    customerId,
}: UseLubricationChartUploadOptions): LubricationChartUpload => {
    const queryClient = useQueryClient();
    const { addLoadingToast } = useToasts();
    const { isProcessing, startProcessing, cancelProcessing } = useLubricationChartProcessing(vesselId, customerId);
    const uploadAbortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        return () => {
            uploadAbortControllerRef.current?.abort();
            cancelProcessing();
        };
    }, [cancelProcessing, vesselId]);

    const uploadLubricationChart = async (file: File) => {
        const abortController = new AbortController();
        uploadAbortControllerRef.current = abortController;

        try {
            const processingPromise = startProcessing();

            return await addLoadingToast(
                async () => {
                    await createLubricationChart({ vesselId, file });
                    await queryClient.invalidateQueries({ queryKey: queries.vessels.detail(vesselId).queryKey });
                    return processingPromise;
                },
                translate('vessel.products.toasts.uploading'),
                (productCount) => {
                    if (productCount === 0) {
                        return translate('vessel.products.toasts.noProductsFound');
                    }
                    return translate('vessel.products.toasts.uploadSuccess', {
                        count: productCount.toString(),
                    });
                },
                translate('vessel.products.toasts.uploadError'),
                { signal: abortController.signal }
            );
        } catch (error) {
            cancelProcessing();
            throw error;
        } finally {
            if (uploadAbortControllerRef.current === abortController) {
                uploadAbortControllerRef.current = null;
            }
        }
    };

    return {
        isProcessing,
        uploadLubricationChart,
    };
};
